import { useEffect, useRef } from "react";
import { markerSize, motoNotch, vehicleMarker } from "../car.ts";
import { createTraffic, pointAlong, ROAD_PATHS } from "./live-map.ts";
import { CITIES, LABELS, MAP_H, MAP_W, project, STATE_PATH } from "./sp-map.ts";

const CARS = 22;
const MOTOS = 12;
/** Fewer on a phone, where the same map is a third of the size. */
const CARS_SMALL = 10;
const MOTOS_SMALL = 7;
const SMALL_BELOW = 640;
const COMET = 64;
const MARKER_SCALE_MIN = 0.42;
const MARKER_SCALE_MAX = 0.56;
const VIEW_BOX = `0 0 ${MAP_W} ${MAP_H}`;
const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";

const CAR_WHITE = vehicleMarker("car", "branco", 0);
const CAR_BLACK = vehicleMarker("car", "preto", 0);
const MOTO = vehicleMarker("motorcycle", null, null);
const MOTO_NOTCH = motoNotch();

/** Everything that doesn't move, in its own SVG so the moving layer's repaints never redraw it. */
function BaseMap() {
  const home = project(CITIES.saoPaulo.at);
  return (
    <svg className="lm-layer" viewBox={VIEW_BOX} role="img" aria-label="Mapa ilustrativo do estado de São Paulo, com carros e motos simulados percorrendo as principais rodovias">
      <defs>
        <pattern id="lm-dots" width="11" height="11" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1" fill="#ffb98f" fillOpacity="0.17" />
        </pattern>
        <radialGradient id="lm-heat" gradientUnits="userSpaceOnUse" cx={home.x} cy={home.y} r="430">
          <stop offset="0" stopColor="#ff6b1a" stopOpacity="0.3" />
          <stop offset="1" stopColor="#ff6b1a" stopOpacity="0" />
        </radialGradient>
      </defs>
      <path d={STATE_PATH} className="lm-halo" />
      <path d={STATE_PATH} className="lm-state" />
      <path d={STATE_PATH} fill="url(#lm-dots)" />
      <path d={STATE_PATH} fill="url(#lm-heat)" />
      <path d={STATE_PATH} className="lm-coast" />
      <g className="lm-roads">
        {ROAD_PATHS.map((d) => <path key={d} d={d} />)}
      </g>
      <g className="lm-cities">
        {Object.values(CITIES).map(({ at }) => {
          const p = project(at);
          return <circle key={`${p.x} ${p.y}`} cx={p.x} cy={p.y} r="1.9" />;
        })}
      </g>
    </svg>
  );
}

/** The names go over the traffic, so a passing car never blurs them. */
function Names() {
  return (
    <svg className="lm-layer" viewBox={VIEW_BOX} aria-hidden="true">
      {LABELS.map((l) => {
        const at = project(CITIES[l.city].at);
        return (
          <g key={l.city} className={l.phone ? "lm-place lm-place-phone" : "lm-place"}>
            <circle cx={at.x} cy={at.y} r="3" />
            <text x={at.x + l.dx} y={at.y + l.dy} textAnchor={l.anchor}>{l.name}</text>
          </g>
        );
      })}
    </svg>
  );
}

const asSlots = <T extends Element>(group: Element | null) => (group ? [...group.children] as T[] : []);

/**
 * Trips happening over São Paulo state, simulated (live-map.ts) and labelled as such on the page. Runs
 * only while on screen. With reduced motion it draws one still moment of the traffic and stops there.
 */
export function LiveMap() {
  const box = useRef<HTMLDivElement>(null);
  const routesRef = useRef<SVGGElement>(null);
  const vehiclesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const small = el.clientWidth < SMALL_BELOW;
    const cars = small ? CARS_SMALL : CARS;
    const motos = small ? MOTOS_SMALL : MOTOS;
    const traffic = createTraffic({ cars, motos });
    // A head start, so the map is already busy the first time it's seen.
    for (let i = 0; i < 16 * 30; i++) traffic.step(1 / 30);

    const routes = asSlots<SVGGElement>(routesRef.current);
    const markers = asSlots<HTMLDivElement>(vehiclesRef.current);
    const carSlots = markers.filter((g) => g.dataset.kind === "car");
    const motoSlots = markers.filter((g) => g.dataset.kind === "moto");
    const carRoutes = routes.filter((g) => g.dataset.kind === "car");
    const motoRoutes = routes.filter((g) => g.dataset.kind === "moto");
    const slots = traffic.vehicles.map((v, i) => {
      const route = v.kind === "car" ? carRoutes[i] : motoRoutes[i - cars];
      const marker = v.kind === "car" ? carSlots[i] : motoSlots[i - cars];
      if (!route || !marker) throw new Error("LiveMap: more vehicles than slots");
      marker.style.display = "";
      return {
        v,
        route,
        marker,
        line: route.querySelector(".lm-line")!,
        glow: route.querySelector(".lm-glow")!,
        comet: route.querySelector(".lm-comet")!,
        from: route.querySelector(".lm-from")!,
        to: route.querySelector(".lm-to")!,
        ripple: route.querySelector<SVGCircleElement>(".lm-ripple")!,
        notch: marker.querySelector<HTMLImageElement>(".lm-notch"),
        size: 0,
        tripNumber: -1,
        phase: v.phase,
        on: false,
      };
    });

    let toPixels = 1;
    const resize = () => {
      toPixels = el.clientWidth / MAP_W;
      // The apps' proportions between the two markers, scaled to a map that shows a whole state.
      const scale = Math.min(MARKER_SCALE_MAX, Math.max(MARKER_SCALE_MIN, el.clientWidth / 1500));
      for (const s of slots) {
        s.size = markerSize(s.v.kind === "car" ? "car" : "motorcycle") * scale;
        s.marker.style.width = s.marker.style.height = `${s.size.toFixed(1)}px`;
      }
    };
    resize();

    const ripple = (s: (typeof slots)[number], x: number, y: number) => {
      if (reduce) return;
      s.ripple.setAttribute("cx", x.toFixed(1));
      s.ripple.setAttribute("cy", y.toFixed(1));
      s.ripple.animate?.(
        [{ transform: "scale(0.3)", opacity: 0.9 }, { transform: "scale(1)", opacity: 0 }],
        { duration: 900, easing: EASE_OUT },
      );
    };

    const draw = () => {
      for (const s of slots) {
        const { v } = s;
        if (s.tripNumber !== v.tripNumber) {
          s.tripNumber = v.tripNumber;
          const length = v.trip.length.toFixed(1);
          for (const p of [s.line, s.glow, s.comet]) p.setAttribute("d", v.trip.path);
          for (const p of [s.glow, s.comet]) p.setAttribute("stroke-dasharray", `${COMET} ${length}`);
          const start = pointAlong(v.trip, 0);
          const end = pointAlong(v.trip, v.trip.length);
          s.from.setAttribute("cx", start.x.toFixed(1));
          s.from.setAttribute("cy", start.y.toFixed(1));
          s.to.setAttribute("cx", end.x.toFixed(1));
          s.to.setAttribute("cy", end.y.toFixed(1));
        }
        if (s.phase !== v.phase) {
          if (v.phase !== "waiting") ripple(s, v.x, v.y);
          s.phase = v.phase;
        }
        const on = v.phase !== "arrived";
        if (s.on !== on) {
          s.on = on;
          s.route.dataset.on = String(on);
        }
        const offset = (COMET - v.s).toFixed(1);
        s.glow.setAttribute("stroke-dashoffset", offset);
        s.comet.setAttribute("stroke-dashoffset", offset);
        const place = `translate3d(${(v.x * toPixels - s.size / 2).toFixed(2)}px, ${(v.y * toPixels - s.size / 2).toFixed(2)}px, 0)`;
        const turn = `rotate(${v.heading.toFixed(1)}deg)`;
        // As in the apps: a car turns with the road; on a moto only the notch does, the helmet stays upright.
        if (s.notch) {
          s.marker.style.transform = place;
          s.notch.style.transform = turn;
        } else {
          s.marker.style.transform = `${place} ${turn}`;
        }
      }
    };

    draw();
    const observers = new ResizeObserver(() => {
      resize();
      draw();
    });
    observers.observe(el);
    if (reduce) return () => observers.disconnect();

    let frame = 0;
    let last = 0;
    let visible = false;
    const tick = (now: number) => {
      // A background tab or a long task shouldn't make everything jump.
      traffic.step(Math.min((now - last) / 1000, 1 / 15));
      last = now;
      draw();
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      if (visible && !document.hidden) {
        last = performance.now();
        frame = requestAnimationFrame(tick);
      }
    };
    const seen = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
      sync();
    });
    seen.observe(el);
    document.addEventListener("visibilitychange", sync);
    return () => {
      cancelAnimationFrame(frame);
      seen.disconnect();
      observers.disconnect();
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  const kinds = [...Array<"car">(CARS).fill("car"), ...Array<"moto">(MOTOS).fill("moto")];
  return (
    <div className="lm-map" ref={box}>
      <BaseMap />
      <svg className="lm-layer" viewBox={VIEW_BOX} aria-hidden="true">
        <g ref={routesRef}>
          {kinds.map((kind, i) => (
            <g key={i} className={`lm-route lm-route-${kind}`} data-kind={kind} data-on="false">
              <path className="lm-line" />
              <path className="lm-glow" />
              <path className="lm-comet" />
              <circle className="lm-from" r="4" />
              <circle className="lm-to" r="5" />
              <circle className="lm-ripple" r="26" opacity="0" />
            </g>
          ))}
        </g>
      </svg>
      <div className="lm-markers" ref={vehiclesRef} aria-hidden="true">
        {kinds.map((kind, i) => (
          <div key={i} className="lm-marker" data-kind={kind} style={{ display: "none" }}>
            {kind === "car"
              ? <img src={i % 3 === 2 ? CAR_BLACK : CAR_WHITE} alt="" />
              : <><img className="lm-notch" src={MOTO_NOTCH} alt="" /><img src={MOTO} alt="" /></>}
          </div>
        ))}
      </div>
      <Names />
    </div>
  );
}

/** The map's own section, with the copy beside it and never on top of it. */
export function LiveMapBand() {
  return (
    <section className="lm-band">
      <div className="lm-inner">
        <div className="lm-text">
          <h2>O melhor jeito de viajar</h2>
          <p>Oferecemos os melhores preços com qualidade e seguraça</p>
          <ul className="lm-legend">
            <li><img src={CAR_WHITE} alt="" width={32} height={32} />Carro</li>
            <li><img src={vehicleMarker("motorcycle", null, 0)} alt="" width={28} height={28} />Moto</li>
          </ul>
        </div>
        <LiveMap />
      </div>
    </section>
  );
}
